import { Box, Button, Stack, Typography } from '@mui/material'
import * as monaco from 'monaco-editor'
import { path } from 'ramda'
import { useCallback, useState } from 'react'

import { RecipeTextEditor, useConfirm } from '@/components'
import {
  useGetCustomComponent,
  useUpdateCustomComponent,
} from '@/screens/ComponentLibrary/hooks'

import IModelDecoration = monaco.editor.IModelDecoration
import { useComponentProvider } from '@/components/ViewComponentModal/ViewComponentProvider'
import { useSchema } from '@/hooks'
import { safeCypressID } from '@/utils'

export type RecipeCustomComponent = {
  recipe: string
  name: string
}

type TextEditorState = {
  errors: IModelDecoration[]
  text: string
}

export const RecipeEditor = () => {
  const schema = useSchema({
    name: 'RecipeConfig',
    version: '1.0.0',
  })
  const { componentID } = useComponentProvider()
  const component = useGetCustomComponent<RecipeCustomComponent>(componentID)
  const updateComponent =
    useUpdateCustomComponent<RecipeCustomComponent>(componentID)

  const [recipe, setRecipe] = useState<TextEditorState>({
    errors: [],
    text: component.data?.settings?.recipe ?? '',
  })

  const confirm = useConfirm()
  const saveRecipe = async () => {
    try {
      console.log(recipe.errors)
      if (recipe.errors.length !== 0)
        await confirm({
          confirmationButtonProps: { color: 'primary', variant: 'contained' },
          content: (
            <>
              <Typography>
                {recipe.errors.length > 1
                  ? 'There are some errors in your recipe'
                  : 'There is an error in your recipe'}
              </Typography>
              <Stack>
                {recipe.errors.map((err) => (
                  <Typography key={err.id} variant="caption">
                    {/* @ts-ignore */}
                    {recipe.text.slice(err?.start - 1, err?.end)}
                  </Typography>
                ))}
              </Stack>
              <Typography>Are you sure that you want to save? </Typography>
            </>
          ),
          title: 'Save Recipe',
        })
      await updateComponent.mutateAsync({
        id: componentID,
        settings: {
          recipe: recipe.text,
        },
      })
    } catch (e) {
      console.log(e)
    }
  }

  const generateSchema = useCallback((recipeSchema: object) => {
    const getProperties = (schema: object) => {
      const steps = path(['definitions', 'Data', 'properties', 'steps'], schema)
      return { steps }
    }
    return {
      ...recipeSchema,
      properties: getProperties(recipeSchema),
      required: ['steps'],
    }
  }, [])

  const onChange = useCallback(
    ({ errors, text }: { text: string; errors: IModelDecoration[] }) => {
      setRecipe({ errors, text })
    },
    []
  )
  if (schema.isLoading) return null
  return (
    <Box sx={{ flex: 1, position: 'relative' }}>
      <Button
        id={safeCypressID('save_recipe_code')}
        sx={{
          position: 'absolute',
          right: 0,
          top: 0,
          transform: 'translateY(-36px)',
        }}
        variant="contained"
        onClick={saveRecipe}
      >
        Save
      </Button>
      <RecipeTextEditor
        schema={generateSchema(schema.data)}
        value={recipe.text}
        onChange={onChange}
      />
    </Box>
  )
}
